import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FileQuestion } from 'lucide-react'

import {
  guidanceSearchQueryOptions,
  guidanceSearchTerm,
  moduleGuidanceQueryOptions,
  questionnaireStructureQueryOptions,
} from '#/api/questionnaire-guidance'
import type { QuestionnaireStructure } from '#/api/questionnaire-guidance'
import { CorpusBackLink } from '#/components/corpus/corpus-list'
import { EmptyState } from '#/components/empty-state'
import { FactStrip } from '#/components/corpus/fact-strip'
import { GuidanceReader } from '#/components/corpus/guidance-reader'
import { PageContainer } from '#/components/page-container'
import { PageHeader } from '#/components/page-header'
import { RouteError } from '#/components/route-error'
import { RoutePending } from '#/components/route-pending'
import { useDebouncedValue } from '#/hooks/use-debounced-value'
import { formatNumber } from '#/lib/format'

// The open module, the question a search result opened in it, and the search
// under way, all in the URL, so Back returns from a result to the search.
type GuidanceSearchParams = { module?: string; question?: string; q?: string }

// A search param's text, a number read back as typed (`?q=7.3`).
const optionalText = (value: unknown) =>
  typeof value === 'number'
    ? String(value)
    : typeof value === 'string' && value !== ''
      ? value
      : undefined

// How long typing rests before the search follows it.
const SEARCH_DEBOUNCE_MS = 300

// The module a link names, or the first.
const openModule = (structure: QuestionnaireStructure, moduleId?: string) =>
  structure.modules.find((m) => m.uuid === moduleId) ?? structure.modules[0]

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/corpus/guidance/$code',
)({
  validateSearch: (search: Record<string, unknown>): GuidanceSearchParams => ({
    module: optionalText(search.module),
    question: optionalText(search.question),
    q: optionalText(search.q),
  }),
  loaderDeps: ({ search }) => ({ module: search.module }),
  // The structure is read before the page shows; the open module's guidance
  // starts reading behind it.
  loader: async ({ context, params, deps }) => {
    const structure = await context.queryClient.ensureQueryData(
      questionnaireStructureQueryOptions(params.code),
    )
    const module = openModule(structure, deps.module)
    if (module) {
      void context.queryClient.prefetchQuery(
        moduleGuidanceQueryOptions(params.code, module.uuid),
      )
    }
  },
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: GuidancePage,
})

// One questionnaire's guidance, module by module.
function GuidancePage() {
  const { t, i18n } = useTranslation()
  const { accountId, code } = Route.useParams()
  const { data: structure } = useSuspenseQuery(
    questionnaireStructureQueryOptions(code),
  )
  const questionCount = structure.modules.reduce(
    (n, m) => n + m.questions.length,
    0,
  )

  return (
    <PageContainer width="wide">
      <div className="flex flex-col gap-6">
        <CorpusBackLink accountId={accountId} tab="guidance">
          {t('corpus.guidance.allGuidance')}
        </CorpusBackLink>
        <PageHeader
          title={t('corpus.guidance.name', { questionnaire: structure.name })}
        />
        <FactStrip
          facts={[
            [
              t('corpus.guidance.facts.modules'),
              formatNumber(structure.modules.length, i18n.language),
            ],
            [
              t('corpus.guidance.facts.questions'),
              formatNumber(questionCount, i18n.language),
            ],
          ]}
        />
      </div>

      <Reader structure={structure} />
    </PageContainer>
  )
}

function Reader({ structure }: { structure: QuestionnaireStructure }) {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const q = search.q ?? ''

  // What is typed in the search box that the URL doesn't hold yet; null when
  // the box shows the URL's term. Once typing rests, the URL takes it in place
  // of the current entry.
  const [draft, setDraft] = useState<string | null>(null)
  const typed = useDebouncedValue(draft, SEARCH_DEBOUNCE_MS)
  useEffect(() => {
    if (typed === null || typed !== draft) return
    void navigate({
      search: (prev) => ({
        module: prev.module,
        q: typed.trim() ? typed : undefined,
      }),
      replace: true,
    }).then(() => setDraft((current) => (current === typed ? null : current)))
  }, [typed, draft, navigate])

  const term = guidanceSearchTerm(q)
  const results = useQuery({
    ...guidanceSearchQueryOptions(structure.code, term),
    throwOnError: true,
  })

  const active = openModule(structure, search.module)
  const guidance = useQuery({
    ...moduleGuidanceQueryOptions(structure.code, active?.uuid ?? ''),
    enabled: active !== undefined,
  })

  if (!active) {
    return <EmptyState icon={FileQuestion} title={t('corpus.guidance.empty')} />
  }
  return (
    <GuidanceReader
      modules={structure.modules}
      activeModule={active}
      // Leaving a search for a module is a step Back undoes; moving between
      // modules is not.
      onModuleChange={(module) => {
        setDraft(null)
        void navigate({ search: { module }, replace: term === null })
      }}
      query={draft ?? q}
      onQueryChange={setDraft}
      search={term === null ? null : { term, results: results.data }}
      onOpenResult={(hit) => {
        setDraft(null)
        void navigate({
          search: { module: hit.module_uuid, question: hit.question_uuid },
          resetScroll: false,
        })
      }}
      focusedQuestion={search.question}
      guidance={guidance.isError ? 'error' : guidance.data}
    />
  )
}
